import { supabase, isSupabaseConfigured } from './supabase';
import toast from 'react-hot-toast';

class DatabaseInitializationError extends Error {
  constructor(message, options) {
    super(message);
    this.name = 'DatabaseInitializationError';
    if (options?.cause) {
      this.cause = options.cause;
    }
  }
}

const INITIALIZATION_RPC = 'initialize_prosper_schema';

const getInitializationHelpMessage = () =>
  'Database schema missing. Please run the Supabase SQL migration from the project README to provision the Prosper tables.';

// Database initialization
export const initializeDatabase = async () => {
  if (!isSupabaseConfigured()) {
    console.log('Supabase not configured, using mock data mode');
    return false;
  }

  try {
    // Test connection
    const { data, error } = await supabase.from('contacts_prosper').select('id').limit(1);
    
    if (error) {
      if (error.code === '42P01') {
        // Table doesn't exist, create it via RPC
        await createTables();
        return true;
      }

      throw error;
    }

    return true;
  } catch (error) {
    console.error('Database initialization failed:', error);
    const message =
      error instanceof DatabaseInitializationError
        ? error.message
        : 'Failed to initialize database connection';

    toast.error(message);

    if (error instanceof DatabaseInitializationError) {
      error.displayed = true;
      throw error;
    }

    const initializationError = new DatabaseInitializationError(message, { cause: error });
    initializationError.displayed = true;
    throw initializationError;
  }
};

// Create database tables
export const createTables = async () => {
  try {
    const { error } = await supabase.rpc(INITIALIZATION_RPC);

    if (error) {
      const message =
        error.message && error.message.includes(INITIALIZATION_RPC)
          ? `${getInitializationHelpMessage()} (${error.message})`
          : error.message || 'Failed to create Prosper database tables.';

      throw new DatabaseInitializationError(message, { cause: error });
    }

    console.log('Database tables created successfully');
  } catch (error) {
    if (error instanceof DatabaseInitializationError) {
      throw error;
    }

    const message = getInitializationHelpMessage();
    throw new DatabaseInitializationError(message, { cause: error });
  }
};

// Contacts CRUD operations
export const contactsDB = {
  // Get all contacts
  async getAll() {
    if (!isSupabaseConfigured()) {
      return { data: [], error: null };
    }

    const { data, error } = await supabase
      .from('contacts_prosper')
      .select('*')
      .order('created_at', { ascending: false });

    return { data: data || [], error };
  },

  // Get contact by ID
  async getById(id) {
    if (!isSupabaseConfigured()) {
      return { data: null, error: null };
    }

    const { data, error } = await supabase
      .from('contacts_prosper')
      .select('*')
      .eq('id', id)
      .single();

    return { data, error };
  },

  // Create contact
  async create(contactData) {
    if (!isSupabaseConfigured()) {
      return { data: null, error: { message: 'Supabase not configured' } };
    }

    const { data, error } = await supabase
      .from('contacts_prosper')
      .insert([{
        first_name: contactData.firstName,
        last_name: contactData.lastName,
        email: contactData.email,
        phone: contactData.phone,
        company: contactData.company,
        title: contactData.title,
        recruitment_stage: contactData.recruitmentStage || 'lead',
        interest_level: contactData.interestLevel || 'unknown',
        lead_source: contactData.leadSource,
        location: contactData.location,
        experience: contactData.experience,
        current_production: contactData.currentProduction,
        background: contactData.background,
        status: contactData.status || 'active'
      }])
      .select()
      .single();

    return { data, error };
  },

  // Update contact
  async update(id, updates) {
    if (!isSupabaseConfigured()) {
      return { data: null, error: { message: 'Supabase not configured' } };
    }

    const updateData = {};
    
    // Map frontend field names to database column names
    if (updates.firstName !== undefined) updateData.first_name = updates.firstName;
    if (updates.lastName !== undefined) updateData.last_name = updates.lastName;
    if (updates.email !== undefined) updateData.email = updates.email;
    if (updates.phone !== undefined) updateData.phone = updates.phone;
    if (updates.company !== undefined) updateData.company = updates.company;
    if (updates.title !== undefined) updateData.title = updates.title;
    if (updates.recruitmentStage !== undefined) updateData.recruitment_stage = updates.recruitmentStage;
    if (updates.interestLevel !== undefined) updateData.interest_level = updates.interestLevel;
    if (updates.leadSource !== undefined) updateData.lead_source = updates.leadSource;
    if (updates.location !== undefined) updateData.location = updates.location;
    if (updates.experience !== undefined) updateData.experience = updates.experience;
    if (updates.currentProduction !== undefined) updateData.current_production = updates.currentProduction;
    if (updates.background !== undefined) updateData.background = updates.background;
    if (updates.status !== undefined) updateData.status = updates.status;
    if (updates.lastContact !== undefined) updateData.last_contact = updates.lastContact;
    if (updates.lastContactMethod !== undefined) updateData.last_contact_method = updates.lastContactMethod;
    if (updates.nextAction !== undefined) updateData.next_action = updates.nextAction;
    if (updates.nextActionDate !== undefined) updateData.next_action_date = updates.nextActionDate;

    updateData.updated_at = new Date().toISOString();

    const { data, error } = await supabase
      .from('contacts_prosper')
      .update(updateData)
      .eq('id', id)
      .select()
      .single();

    return { data, error };
  },

  // Delete contact
  async delete(id) {
    if (!isSupabaseConfigured()) {
      return { data: null, error: { message: 'Supabase not configured' } };
    }

    const { error } = await supabase
      .from('contacts_prosper')
      .delete()
      .eq('id', id);

    return { error };
  },

  // Search contacts
  async search(searchTerm) {
    if (!isSupabaseConfigured()) {
      return { data: [], error: null };
    }

    const { data, error } = await supabase
      .from('contacts_prosper')
      .select('*')
      .or(`first_name.ilike.%${searchTerm}%,last_name.ilike.%${searchTerm}%,email.ilike.%${searchTerm}%,company.ilike.%${searchTerm}%`)
      .order('created_at', { ascending: false });

    return { data: data || [], error };
  }
};

// Activities CRUD operations
export const activitiesDB = {
  // Get activities for a contact
  async getByContactId(contactId) {
    if (!isSupabaseConfigured()) {
      return { data: [], error: null };
    }

    const { data, error } = await supabase
      .from('activities_prosper')
      .select('*')
      .eq('contact_id', contactId)
      .order('created_at', { ascending: false });

    return { data: data || [], error };
  },

  // Create activity
  async create(activityData) {
    if (!isSupabaseConfigured()) {
      return { data: null, error: { message: 'Supabase not configured' } };
    }

    const { data, error } = await supabase
      .from('activities_prosper')
      .insert([{
        contact_id: activityData.contactId,
        type: activityData.type,
        title: activityData.title,
        description: activityData.description,
        outcome: activityData.outcome,
        priority: activityData.priority || 'medium',
        details: activityData.details || {},
        user_name: activityData.user || 'System'
      }])
      .select()
      .single();

    return { data, error };
  }
};

// Notes CRUD operations
export const notesDB = {
  // Get notes for a contact
  async getByContactId(contactId) {
    if (!isSupabaseConfigured()) {
      return { data: [], error: null };
    }

    const { data, error } = await supabase
      .from('notes_prosper')
      .select('*')
      .eq('contact_id', contactId)
      .order('created_at', { ascending: false });

    return { data: data || [], error };
  },

  // Create note
  async create(noteData) {
    if (!isSupabaseConfigured()) {
      return { data: null, error: { message: 'Supabase not configured' } };
    }

    const { data, error } = await supabase
      .from('notes_prosper')
      .insert([{
        contact_id: noteData.contactId,
        content: noteData.content,
        author: noteData.author,
        note_type: noteData.type || 'general'
      }])
      .select()
      .single();

    return { data, error };
  }
};

// Tasks CRUD operations
export const tasksDB = {
  // Get tasks for a contact
  async getByContactId(contactId) {
    if (!isSupabaseConfigured()) {
      return { data: [], error: null };
    }

    const { data, error } = await supabase
      .from('tasks_prosper')
      .select('*')
      .eq('contact_id', contactId)
      .order('created_at', { ascending: false });

    return { data: data || [], error };
  },

  // Create task
  async create(taskData) {
    if (!isSupabaseConfigured()) {
      return { data: null, error: { message: 'Supabase not configured' } };
    }

    const { data, error } = await supabase
      .from('tasks_prosper')
      .insert([{
        contact_id: taskData.contactId,
        title: taskData.title,
        description: taskData.description,
        due_date: taskData.dueDate,
        priority: taskData.priority || 'medium'
      }])
      .select()
      .single();

    return { data, error };
  },

  // Update task
  async update(id, updates) {
    if (!isSupabaseConfigured()) {
      return { data: null, error: { message: 'Supabase not configured' } };
    }

    const { data, error } = await supabase
      .from('tasks_prosper')
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    return { data, error };
  }
};

// Meetings CRUD operations
export const meetingsDB = {
  // Get meetings for a contact
  async getByContactId(contactId) {
    if (!isSupabaseConfigured()) {
      return { data: [], error: null };
    }

    const { data, error } = await supabase
      .from('meetings_prosper')
      .select('*')
      .eq('contact_id', contactId)
      .order('scheduled_date', { ascending: true });

    return { data: data || [], error };
  },

  // Create meeting
  async create(meetingData) {
    if (!isSupabaseConfigured()) {
      return { data: null, error: { message: 'Supabase not configured' } };
    }

    const { data, error } = await supabase
      .from('meetings_prosper')
      .insert([{
        contact_id: meetingData.contactId,
        title: meetingData.title,
        description: meetingData.description,
        scheduled_date: meetingData.scheduledDate,
        duration: meetingData.duration || '30 minutes',
        meeting_link: meetingData.meetingLink
      }])
      .select()
      .single();

    return { data, error };
  }
};

// Utility function to transform database contact to frontend format
export const transformContact = (dbContact) => {
  if (!dbContact) return null;

  return {
    id: dbContact.id,
    firstName: dbContact.first_name,
    lastName: dbContact.last_name,
    email: dbContact.email,
    phone: dbContact.phone,
    company: dbContact.company,
    title: dbContact.title,
    recruitmentStage: dbContact.recruitment_stage,
    interestLevel: dbContact.interest_level,
    leadSource: dbContact.lead_source,
    location: dbContact.location,
    experience: dbContact.experience,
    currentProduction: dbContact.current_production,
    background: dbContact.background,
    status: dbContact.status,
    lastContact: dbContact.last_contact,
    lastContactMethod: dbContact.last_contact_method,
    nextAction: dbContact.next_action,
    nextActionDate: dbContact.next_action_date,
    dateAdded: dbContact.created_at,
    createdAt: dbContact.created_at,
    updatedAt: dbContact.updated_at
  };
};

// Utility function to transform database activity to frontend format
export const transformActivity = (dbActivity) => {
  if (!dbActivity) return null;

  return {
    id: dbActivity.id,
    type: dbActivity.type,
    title: dbActivity.title,
    description: dbActivity.description,
    outcome: dbActivity.outcome,
    priority: dbActivity.priority,
    details: dbActivity.details,
    user: dbActivity.user_name,
    timestamp: dbActivity.created_at
  };
};

// Utility function to transform database note to frontend format
export const transformNote = (dbNote) => {
  if (!dbNote) return null;

  return {
    id: dbNote.id,
    content: dbNote.content,
    author: dbNote.author,
    type: dbNote.note_type,
    timestamp: dbNote.created_at
  };
};

// Utility function to transform database task to frontend format
export const transformTask = (dbTask) => {
  if (!dbTask) return null;

  return {
    id: dbTask.id,
    title: dbTask.title,
    description: dbTask.description,
    dueDate: dbTask.due_date,
    status: dbTask.status,
    priority: dbTask.priority,
    createdAt: dbTask.created_at,
    completedAt: dbTask.completed_at
  };
};

// Utility function to transform database meeting to frontend format
export const transformMeeting = (dbMeeting) => {
  if (!dbMeeting) return null;

  return {
    id: dbMeeting.id,
    title: dbMeeting.title,
    description: dbMeeting.description,
    scheduledDate: dbMeeting.scheduled_date,
    duration: dbMeeting.duration,
    meetingLink: dbMeeting.meeting_link,
    status: dbMeeting.status,
    createdAt: dbMeeting.created_at
  };
};